import {useQuery, useMutation, useQueryClient} from '@tanstack/react-query';
import {get, post} from '../../../../services/core';
import React, {useEffect, useId, useRef, useState} from 'react';
import {Badge, Button, Drawer, IconButton} from '@mui/material';
import {CloseRounded, GroupsOutlined, NotificationsNoneRounded, ReceiptLongOutlined} from '@mui/icons-material';
import {useLocation, useNavigate} from 'react-router-dom';
import toast from 'react-hot-toast';
import {useApplicationConfigs} from '../../../../hooks/context/useApplicationConfigs';
import {defaultApplicationConfigsContext} from '../../../../hooks/context/context';
import useAuth from '../../../../hooks/auth/useAuth';
import {ReceiptNotification} from '../../../../services/notifications/notificationModel';
import classes from './NotificationButton.module.scss';
import LoadingIndicator from '../../LoadingIndicator';

const connectionLabels = {
  connecting: 'Connecting…',
  live: 'Live updates',
  reconnecting: 'Reconnecting… Updates may be delayed.',
  offline: 'Offline · Updates paused.',
};

const operationErrors = {
  refresh: 'Could not refresh notifications.',
  loadMore: 'Could not load more notifications.',
  markAllRead: 'Could not mark all notifications as read.',
};
type Operation = keyof typeof operationErrors;

function useNotificationOwner() {
  const {user, isAuthenticated} = useAuth();
  return isAuthenticated && user ? JSON.stringify([user.id ?? user.externalId ?? null, user.username]) : null;
}

// This recovery control lives in the app's toast host, so a failed read is not
// silently lost when opening a receipt unmounts the originating page header.
function ReadFailure({notificationId, owner, toastId}: {notificationId: number; owner: string | null; toastId: string}) {
  const {notificationState} = useApplicationConfigs();
  const currentOwner = useNotificationOwner();
  const location = useLocation();
  const [pending, setPending] = useState(false);
  const [failedAgain, setFailedAgain] = useState(false);
  const retrying = useRef(false);
  const available = owner !== null && owner === currentOwner && !/^\/shared(?:\/|$)/i.test(location.pathname);
  const dismiss = () => toast.dismiss(toastId);
  useEffect(() => { if (!available) toast.dismiss(toastId); }, [available, toastId]);
  if (!available) return null;
  return <div className={classes.readFailure}>
    <p role="alert">{failedAgain ? 'Still unable to mark as read. Please try again.' : 'Could not mark notification as read.'}</p>
    <Button disabled={pending} onClick={async () => {
      if (retrying.current) return;
      retrying.current = true;
      setPending(true);
      try {
        await (notificationState ?? defaultApplicationConfigsContext.notificationState).markRead(notificationId);
        dismiss();
      } catch {
        setFailedAgain(true);
      } finally {
        retrying.current = false;
        setPending(false);
      }
    }}>{pending ? 'Retrying…' : 'Retry marking as read'}</Button>
    <Button onClick={dismiss}>Dismiss</Button>
  </div>;
}

const NotificationButton = () => {
  const configs = useApplicationConfigs();
  const state = configs.notificationState ?? defaultApplicationConfigsContext.notificationState;
  const {items, unreadCount, loading, error, connectionStatus, hasMore, loadingMore, markingRead} = state;
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState(false);
  const [failure, setFailure] = useState<Operation | null>(null);
  const owner = useNotificationOwner();
  const client = useQueryClient();
  const invitations = useQuery<{id: number; groupId: number; groupName: string; invitedBy: string}[]>(
    ['invitations', owner], () => get('/v1/invitations'), {enabled: !!owner, refetchInterval: 30000});
  const decision = useMutation(({id, accept}: {id: number; accept: boolean}) => post('/v1/invitations/' + id, {accept}), {
    onSuccess: () => {
      for (const key of ['invitations', 'groups', 'groupDetails', 'history', 'getStatistics']) void client.invalidateQueries([key]);
      void state.refresh().catch(() => setFailure('refresh'));
    },
  });
  const invitationCount = invitations.data?.length ?? 0;
  const currentOwner = useRef(owner);
  currentOwner.current = owner;
  const busy = useRef(false);
  const reads = useRef(new Map<number, Promise<void>>());
  const readIds = useRef(new Set<number>());
  const navigate = useNavigate();
  const location = useLocation();
  const titleId = useId();

  useEffect(() => { setOpen(false); }, [location.key]);
  useEffect(() => {
    setOpen(false);
    setFailure(null);
    setPending(false);
    busy.current = false;
    reads.current.clear();
    readIds.current.clear();
  }, [owner]);

  const run = async (operation: Operation) => {
    if (busy.current) return;
    busy.current = true;
    setPending(true);
    try {
      await state[operation]();
      if (currentOwner.current === owner) setFailure(null);
    } catch {
      if (currentOwner.current === owner) setFailure(operation);
    } finally {
      if (currentOwner.current === owner) {
        busy.current = false;
        setPending(false);
      }
    }
  };

  const markRead = (item: ReceiptNotification): Promise<void> => {
    if (item.readAt !== null || readIds.current.has(item.id)) return Promise.resolve();
    const existing = reads.current.get(item.id);
    if (existing) return existing;
    const promise = Promise.resolve().then(() => state.markRead(item.id)).then(() => {
      if (currentOwner.current === owner) readIds.current.add(item.id);
    }).finally(() => { if (currentOwner.current === owner) reads.current.delete(item.id); });
    reads.current.set(item.id, promise);
    return promise;
  };

  const openReceipt = (item: ReceiptNotification) => {
    setOpen(false);
    navigate(`/receipt/${item.receiptId}`);
    void markRead(item).catch(() => {
      toast(t => <ReadFailure notificationId={item.id} owner={owner} toastId={t.id}/>, {
        id: `notification-read-${item.id}`,
        duration: Infinity,
        ariaProps: {role: 'status', 'aria-live': 'off'},
      });
    });
  };

  const controlsPending = pending || markingRead || loading || loadingMore;
  return <>
    <IconButton data-tour="notifications"
      className={classes.bell}
      sx={{width: 44, height: 44, flexShrink: 0, color: 'inherit'}}
      aria-label={`Notifications, ${unreadCount + invitationCount} unread`}
      aria-haspopup="dialog"
      aria-expanded={open}
      onClick={() => setOpen(true)}
    >
      <Badge badgeContent={unreadCount + invitationCount} max={99} className={classes.badge}>
        <NotificationsNoneRounded aria-hidden="true"/>
      </Badge>
    </IconButton>
    <Drawer anchor="right" open={open} onClose={() => setOpen(false)}
            PaperProps={{className: classes.panel, role: 'dialog', 'aria-modal': true, 'aria-labelledby': titleId}}
            sx={{'& .MuiDrawer-paper': {width: '100%', maxWidth: 420, boxSizing: 'border-box'}}}>
      <header className={classes.header}>
        <div className={classes.heading}>
          <div><span className={classes.eyebrow}>Your activity</span><h2 id={titleId}>Notifications</h2></div>
          <IconButton autoFocus aria-label="Close notifications" onClick={() => setOpen(false)}
                      sx={{width: 44, height: 44, color: 'inherit'}}>
            <CloseRounded/>
          </IconButton>
        </div>
        <p className={classes.connection} role="status" aria-live="polite" aria-atomic="true">
          <span className={classes.connectionDot} data-status={connectionStatus} aria-hidden="true"/>
          {connectionLabels[connectionStatus]}
        </p>
      </header>
      <div className={classes.summary}>
        <p>{unreadCount + invitationCount === 0 ? 'No unread notifications' : `${unreadCount + invitationCount} unread`}</p>
        <Button disabled={unreadCount === 0 || controlsPending}
                onClick={() => void run('markAllRead')}>
          {markingRead || pending ? 'Please wait…' : 'Mark all as read'}
        </Button>
      </div>
      <div className={classes.content}>
        {invitations.isError && <p role="alert">Could not load invitations. <Button onClick={() => invitations.refetch()}>Retry invitations</Button></p>}
        {decision.isError && <p role="alert">Could not respond to the invitation. Please try again.</p>}
        {invitationCount > 0 && <h3 className={classes.sectionTitle}>Group invitations <span>{invitationCount}</span></h3>}
        <ul className={classes.list} aria-label="Group invitations">
          {(invitations.data ?? []).map(invite => <li className={`${classes.card} ${classes.invitation}`} key={invite.id}>
            <div className={classes.invitationHeading}>
              <span className={classes.receiptIcon}><GroupsOutlined aria-hidden="true"/></span>
              <div className={classes.details}><strong className={classes.receiptName}>{invite.groupName}</strong>
                <p className={classes.actor}><strong>{invite.invitedBy}</strong> invited you to join</p></div>
            </div>
            <div className={classes.invitationActions}>
              <Button className={classes.accept} disabled={decision.isLoading} onClick={() => decision.mutate({id: invite.id, accept: true})}>Accept</Button>
              <Button className={classes.decline} disabled={decision.isLoading} onClick={() => decision.mutate({id: invite.id, accept: false})}>Decline</Button>
            </div>
          </li>)}
        </ul>
        {(failure || error) && <div className={classes.error}>
          <p role="alert">{failure ? operationErrors[failure] : error}</p>
          <Button disabled={controlsPending}
                  onClick={() => void run(failure ?? 'refresh')}>
            Retry
          </Button>
        </div>}
        {loading && <LoadingIndicator label="Loading notifications" className={classes.message}/>}
        {!loading && !error && !failure && items.length === 0 && invitationCount === 0 && <div className={classes.empty}>
          <NotificationsNoneRounded aria-hidden="true"/>
          <h3>You’re all caught up</h3>
          <p>When someone shares a receipt with you, it will appear here.</p>
        </div>}
        {items.length > 0 && <h3 className={classes.sectionTitle}>Shared receipts</h3>}
        <ul className={classes.list} aria-label="Receipt notifications" aria-busy={loading}>
          {items.map(item => {
            const date = new Date(item.createdAt);
            const validDate = !Number.isNaN(date.getTime());
            const validReceipt = Number.isSafeInteger(item.receiptId) && item.receiptId > 0;
            const unread = item.readAt === null;
            return <li key={item.id} className={classes.card} data-unread={unread}>
              <button className={classes.entry} disabled={!validReceipt} onClick={() => openReceipt(item)}
                      aria-label={`${unread ? 'Unread. ' : ''}${item.actorName} shared ${item.receiptName}, ${item.groupName}. Open receipt`}>
                <span className={classes.receiptIcon}><ReceiptLongOutlined aria-hidden="true"/></span>
                <span className={classes.details}>
                  <span className={classes.actor}><strong>{item.actorName}</strong> shared a receipt</span>
                  <strong className={classes.receiptName}>{item.receiptName}</strong>
                  <span className={classes.group}>{item.groupName}</span>
                  {validDate ? <time dateTime={item.createdAt}>{date.toLocaleString(undefined, {
                    year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
                  })}</time> : <span className={classes.group}>Date unavailable</span>}
                  {!validReceipt && <span className={classes.group}>Receipt unavailable</span>}
                </span>
                {unread && <span className={classes.unreadDot} aria-hidden="true"/>}
              </button>
            </li>;
          })}
        </ul>
        {hasMore && <Button className={classes.loadMore} disabled={controlsPending}
                            aria-label={loadingMore ? 'Loading more notifications' : undefined}
                            onClick={() => void run('loadMore')}>
          {loadingMore ? <LoadingIndicator label="Loading more notifications" variant="inline"/> : 'Load more'}
        </Button>}
        <Button className={classes.refresh} disabled={controlsPending}
                onClick={() => void run('refresh')}>Refresh notifications</Button>
      </div>
    </Drawer>
  </>;
};

export default NotificationButton;
